<template>
    <div v-if="course || courseTemplate">
        <ul>
            <li class="is-sidebar-summary-item is-collection">
                <ul class="tw:justify-center tw:items-center">
                    <app-sidebar-summary-item
                        v-if="!isCourseTemplate"
                        id="application_sidebar_summary_course_dashboard"
                        type="is-primary"
                        label="Course Dashboard"
                        :to="{ name: 'course-dashboard', params: { courseId } }"
                    >
                        <template #icon>
                            <ph-squares-four :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="canShowGradebookPreview"
                        type="is-primary"
                        label="Gradebook Preview"
                        :to="{
                            name: 'course-template-gradebook-preview',
                            params: { id: $route.params.id },
                        }"
                    >
                        <template #icon>
                            <ph-chart-line-up :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-else-if="
                            !isCorporateClientMode &&
                                !isCourseTemplate &&
                                course &&
                                $gate.allows('viewPreview', ['Gradebook', course])
                        "
                        type="is-primary"
                        label="Gradebook Preview"
                        :to="{
                            name: 'course-gradebook-preview',
                            params: { courseId },
                        }"
                    >
                        <template #icon>
                            <ph-chart-line-up :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <template v-if="!isCorporateClientMode">
                        <app-sidebar-summary-item
                            v-if="!isCourseTemplate && $gate.allows('view', ['Gradebook', course])"
                            type="is-primary"
                            :label="gradebookMenuLabel"
                            :to="{
                                name: 'gradebook-view',
                                params: {
                                    courseId,
                                    gradebookId: get(course, 'gradebook.id'),
                                },
                            }"
                        >
                            <template #icon>
                                <ph-chart-bar :size="20" weight="bold" />
                            </template>
                        </app-sidebar-summary-item>

                        <app-sidebar-summary-item
                            v-if="!isCourseTemplate && $gate.allows('viewStudent', ['Gradebook', course])"
                            type="is-primary"
                            label="Grades"
                            :to="{
                                name: 'student-grade-view',
                                params: { courseId: course.id, userId: user.id },
                            }"
                        >
                            <template #icon>
                                <ph-chart-bar :size="20" weight="bold" />
                            </template>
                        </app-sidebar-summary-item>

                        <app-sidebar-summary-item
                            v-if="
                                !isCourseTemplate &&
                                    $gate.allows('viewCourseEnrollmentPage', ['CourseEnrollment', course])
                            "
                            type="is-primary-dark"
                            label="Enrollments"
                            :to="{
                                name: 'course-enrollments-view',
                                params: { courseId },
                            }"
                        >
                            <template #icon>
                                <ph-users :size="20" weight="bold" />
                            </template>
                        </app-sidebar-summary-item>
                    </template>
                </ul>
            </li>

            <li
                v-if="user && course && (user.hasPermissionViaCourseTo(course, 'view a course') || user.is_admin)"
                class="is-sidebar-summary-item is-collection"
            >
                <ul v-if="shouldDisplayCourseContentMenuItems">
                    <!--
                        One control only: Calendar for time-based courses with week buckets
                        (opens Active / Upcoming / Previous weeks). Folder when the course
                        has non-week content and no week buckets to navigate.
                        isTimeBasedCourse && (!hasNonWeekCourseContent || !isMenuForCurrentCourse)
                        covers first paint and course→course navigation: the course is in ORM
                        before /menu hydrates (or the store still holds the previous course's
                        menu), so we show Calendar instead of a transient Folder — but
                        time-based courses that permanently have only misc content still use
                        Folder once the loaded menu is stamped for this courseId.
                    -->
                    <app-sidebar-summary-item
                        v-if="shouldShowWeeksSummaryItem"
                        :class="{ 'is-active': hasPresentWeek }"
                        label="Click to View All Weeks"
                        type="is-primary-light"
                        :icon="getWeeksIcon"
                        :title="getWeeksTitle"
                        @open="open(weeksSummaryOpenKey)"
                    />

                    <app-sidebar-summary-item
                        v-else-if="hasNonWeekCourseContent"
                        type="is-primary-light"
                        label="Click to Expand"
                        @open="open(courseContentSummaryOpenKey)"
                    >
                        <template #icon>
                            <ph-folder-open :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>
                </ul>
                <ul v-else>
                    <ph-folder-open :size="20" weight="bold" class="tw:text-gray-400 tw:cursor-not-allowed" />
                </ul>
            </li>

            <li
                v-if="isCorporateClientMode && !isCourseTemplate && course"
                class="is-sidebar-summary-item is-collection"
            >
                <ul class="tw:justify-center tw:items-center">
                    <app-sidebar-summary-item
                        v-if="$gate.allows('viewStudent', ['Gradebook', course])"
                        type="is-primary"
                        label="Grades"
                        icon="pi pi-chart-bar"
                        :to="{
                            name: 'student-grade-view',
                            params: { courseId: course.id, userId: user.id },
                        }"
                    />

                    <app-sidebar-summary-item
                        v-if="$gate.allows('viewCourseEnrollmentPage', ['CourseEnrollment', course])"
                        type="is-primary-dark"
                        :label="enrollmentProgressMenuLabel"
                        icon="pi pi-users"
                        :to="{
                            name: 'course-progress-view',
                            params: { courseId },
                        }"
                    />
                </ul>
            </li>

            <li v-if="!isCourseTemplate && shouldShowSecondaryCourseMenu" class="is-sidebar-summary-item is-collection">
                <ul>
                    <app-sidebar-summary-item
                        v-if="course && !isSelfPacedCourse && $gate.allows('viewAny', ['Attendance', course])"
                        label="Attendance"
                        type="is-primary-dark"
                        :to="{ name: 'course-attendance' }"
                    >
                        <template #icon>
                            <ph-address-book-tabs :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="course && !isCorporateClientMode && $can('view all', 'meetings')"
                        label="Meetings"
                        type="is-primary-dark"
                        :active="isMeetingsSectionActive"
                        :to="{ name: 'course-meetings-view-upcoming' }"
                    >
                        <template #icon>
                            <ph-presentation :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="
                            course &&
                                !isCorporateClientMode &&
                                $gate.allows('viewAny', ['AttemptAuthorization', course])
                        "
                        label="Attempt Authorizations"
                        type="is-primary-dark"
                        :to="{ name: 'course-attempt-authorizations-view' }"
                    >
                        <template #icon>
                            <ph-key :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="$gate.allows('viewAny', ['QuizDashboard'])"
                        label="Quiz Dashboard"
                        type="is-primary-dark"
                        :to="{ name: 'quiz-dashboard' }"
                    >
                        <template #icon>
                            <ph-chalkboard-teacher :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="course && !isCorporateClientMode && hasNotesMenu"
                        label="My Notes"
                        type="is-primary-dark"
                        :to="{ name: 'course-notes' }"
                    >
                        <template #icon>
                            <ph-notepad :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="course && !isCorporateClientMode && $gate.allows('viewReports', course)"
                        label="Course Reports"
                        type="is-primary"
                        :to="{ name: 'discussion-thread-report' }"
                    >
                        <template #icon>
                            <ph-presentation-chart :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="course && !isCorporateClientMode && $gate.allows('viewAny', 'Remediation')"
                        label="Remediation"
                        type="is-primary"
                        :to="{ name: 'course-remediation' }"
                        :has-badge="canViewRemediation"
                        :badge-count="pendingGradeRemediations.length"
                    >
                        <template #icon>
                            <ph-receipt :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="course && !isCorporateClientMode && $gate.allows('viewAny', ['CourseLogEntry', course])"
                        label="Course Log"
                        type="is-primary"
                        :has-badge="get(course, 'log_entries_count', 0) > 0"
                        :badge-count="get(course, 'log_entries_count', 0)"
                        @open="openCourseLog = true"
                    >
                        <template #icon>
                            <ph-cassette-tape :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>

                    <app-sidebar-summary-item
                        v-if="!isCorporateClientMode && course && canShowScheduleMenu"
                        :label="scheduleMenuLabel"
                        type="is-primary"
                        :to="{ name: scheduleMenuRouteName }"
                    >
                        <template #icon>
                            <ph-calendar-dots :size="20" weight="bold" />
                        </template>
                    </app-sidebar-summary-item>
                </ul>
            </li>
        </ul>

        <e-modal v-if="courseId" v-model="openCourseLog" width="700" has-modal-card>
            <course-log-entries :course-id="courseId" />
        </e-modal>
    </div>
</template>

<script>
import {
    PhAddressBookTabs,
    PhCalendarDots,
    PhCassetteTape,
    PhChalkboardTeacher,
    PhChartBar,
    PhChartLineUp,
    PhFolderOpen,
    PhKey,
    PhNotepad,
    PhPresentation,
    PhPresentationChart,
    PhReceipt,
    PhSquaresFour,
    PhUsers,
} from '@phosphor-icons/vue';
import get from 'lodash/get.js';
import { mapState } from 'vuex';

import AppSidebarSummaryItem from '@/components/app/sidebar/AppSidebarSummaryItem.vue';
import CourseLogEntries from '@/components/course-log/CourseLogEntries.vue';
import { isCorporateClient } from '@/composables/useEdverumClient.js';
import { isCourseSelfPaced } from '@/composables/useSelfPacedCourse.js';
import {
    CORPORATE_ENROLLMENT_PROGRESS_MENU_LABEL,
    CORPORATE_GRADEBOOK_MENU_LABEL,
    DEFAULT_GRADEBOOK_MENU_LABEL,
} from '@/constants/corporateCourseMenu.js';
import Course from '@/models/Course.js';
import CourseTemplate from '@/models/CourseTemplate.js';
import Remediation from '@/models/Remediation.js';
import { nestedListItemKey } from '@/utilities/nestedListItemKey.js';
import { isCourseMeetingsRouteName } from '@/utilities/sidebarMenuRoute.js';

export default {
    name: 'AppSidebarSummary',

    components: {
        AppSidebarSummaryItem,
        CourseLogEntries,
        PhAddressBookTabs,
        PhCalendarDots,
        PhCassetteTape,
        PhChalkboardTeacher,
        PhChartBar,
        PhChartLineUp,
        PhFolderOpen,
        PhKey,
        PhNotepad,
        PhPresentation,
        PhPresentationChart,
        PhReceipt,
        PhSquaresFour,
        PhUsers,
    },

    props: {
        items: {
            default: null,
            type: null,
        },

        hasNotesMenu: {
            default: false,
            type: Boolean,
        },

        hasCourseReportsMenu: {
            default: false,
            type: Boolean,
        },

        courseId: {
            type: Number,
            required: true,
        },
    },

    emits: ['open'],

    data: () => ({
        openCourseLog: false,
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),
        ...mapState('Menu', { menuCourseId: state => state.menuCourseId }),

        shouldDisplayCourseContentMenuItems() {
            const hasMeta = this.items.meta?.length > 0;

            return (
                hasMeta ||
                this.items.curriculum.misc_pre.length ||
                this.hasWeeksItem ||
                this.isTimeBasedCourse ||
                this.items.curriculum.misc_post.length
            );
        },

        isCourseTemplate() {
            return this.$route.name.includes('template');
        },

        /**
         * @returns {boolean}
         */
        hasWeeksItem() {
            const { weeks } = this.items.curriculum;

            return weeks.past.length || weeks.present.length || weeks.future.length;
        },

        /**
         * Non-week curriculum content (orientation, resources, etc.).
         * Used only when the course is not time-based (no week buckets) —
         * Calendar and Folder are mutually exclusive.
         *
         * @returns {boolean}
         */
        hasNonWeekCourseContent() {
            const { misc_pre: miscPre, misc_post: miscPost } = this.items.curriculum;

            return Boolean(miscPre?.length || miscPost?.length);
        },

        /**
         * Whether dynamicMenu in the store was loaded for the course currently on screen.
         *
         * @returns {boolean}
         */
        isMenuForCurrentCourse() {
            return this.menuCourseId != null && Number(this.menuCourseId) === Number(this.courseId);
        },

        /**
         * Calendar summary control: week buckets when present, or a placeholder on first
         * paint / mid-navigation for time-based courses before this course's /menu hydrates.
         * Suppressed when the loaded menu belongs to this course and has only non-week content
         * (no week sections) so Folder can open that content.
         *
         * @returns {boolean}
         */
        hasWeeksItemForCurrentCourse() {
            return this.isMenuForCurrentCourse && this.hasWeeksItem;
        },

        shouldShowWeeksSummaryItem() {
            return (
                this.hasWeeksItemForCurrentCourse ||
                (this.isTimeBasedCourse && (!this.hasNonWeekCourseContent || !this.isMenuForCurrentCourse))
            );
        },

        /**
         * Break weeks use sunset; regular weeks use calendar-alt (Unicons via e-icon).
         *
         * @returns {string}
         */
        getWeeksIcon() {
            if (!this.isMenuForCurrentCourse) {
                return 'calendar-alt';
            }

            if (this.hasPresentWeek && this.items.curriculum.weeks.present[0].section_type === 'break') {
                return 'sunset';
            }

            return 'calendar-alt';
        },

        /**
         * @returns {string|null}
         */
        getWeeksTitle() {
            if (!this.isMenuForCurrentCourse || !this.hasWeeksItem) return null;

            let { weeks } = this.items.curriculum;

            if (this.hasPresentWeek) {
                const presentWeek = weeks.present[0];

                if (presentWeek.section_type === 'break') {
                    return 'Break';
                }

                return `Week ${presentWeek.display_week_number}`;
            }

            if (weeks.past.length) {
                weeks = weeks.past;
            } else if (weeks.future.length) {
                weeks = weeks.future;
            } else {
                return null;
            }

            weeks = weeks.filter(week => week.section_type === 'week');

            // Past/future buckets may only contain breaks (common on break-heavy curricula).
            // Do not read display_week_number from an empty filtered list.
            if (!weeks.length) {
                return null;
            }

            return weeks.length > 1
                ? `Weeks ${weeks[0].display_week_number}-${weeks[weeks.length - 1].display_week_number}`
                : `Week ${weeks[0].display_week_number}`;
        },

        /**
         * @returns {boolean}
         */
        hasPresentWeek() {
            if (!this.isMenuForCurrentCourse || !this.hasWeeksItem) return false;

            return !!this.items.curriculum.weeks.present.length;
        },

        /**
         * Open key for the Calendar summary control.
         * Prefer Active (present) week, then Previous Weeks, then Upcoming Weeks.
         *
         * @returns {string|undefined}
         */
        weeksSummaryOpenKey() {
            if (!this.isMenuForCurrentCourse || !this.hasWeeksItem) {
                return undefined;
            }

            const { weeks } = this.items.curriculum;

            if (this.hasPresentWeek) {
                return this.nestedListItemKey('current-weeks', weeks.present[0]);
            }

            if (weeks.past.length) {
                return 'previous-weeks';
            }

            if (weeks.future.length) {
                return 'upcoming-weeks';
            }

            return undefined;
        },

        /**
         * Open key for the Folder summary control (non-time-based courses).
         *
         * @returns {string|undefined}
         */
        courseContentSummaryOpenKey() {
            const { misc_pre: miscPre, misc_post: miscPost } = this.items.curriculum;

            if (miscPre?.length) {
                return this.nestedListItemKey('course-content', miscPre[0]);
            }

            if (miscPost?.length) {
                return this.nestedListItemKey('additional-content', miscPost[0]);
            }

            return undefined;
        },

        course() {
            return Course.query().with('gradebook').find(this.courseId);
        },

        isSelfPacedCourse() {
            return isCourseSelfPaced(this.course);
        },

        /**
         * Course type is on the Course row as soon as it is inserted — before
         * week buckets land in the menu store. Use that so time-based courses
         * render Calendar on first paint instead of the empty-state Folder.
         *
         * @returns {boolean}
         */
        isTimeBasedCourse() {
            if (!this.course || this.isSelfPacedCourse) {
                return false;
            }

            return Boolean(this.course.course_type?.title || this.course.course_type_id != null);
        },

        courseTemplate() {
            return CourseTemplate.query().find(this.$route.params.id);
        },

        canViewRemediation() {
            return this.user.hasPermissionTo('view all remediation processes');
        },

        remediations() {
            return Remediation.query().where('course_id', this.courseId).get();
        },

        pendingGradeRemediations() {
            return this.remediations.filter(remediation => {
                return remediation.status === 'Pending Instructor Grade';
            });
        },

        canShowScheduleMenu() {
            // Corporate hides Schedules and Due Dates from the course menu entirely.
            if (this.isCorporateClientMode) {
                return false;
            }

            return this.$gate.allows('viewAny', 'TimePeriod');
        },

        isCorporateClientMode() {
            return isCorporateClient();
        },

        gradebookMenuLabel() {
            return this.isCorporateClientMode ? CORPORATE_GRADEBOOK_MENU_LABEL : DEFAULT_GRADEBOOK_MENU_LABEL;
        },

        enrollmentProgressMenuLabel() {
            return CORPORATE_ENROLLMENT_PROGRESS_MENU_LABEL;
        },

        shouldShowSecondaryCourseMenu() {
            if (this.isCorporateClientMode) {
                return (
                    (!this.isSelfPacedCourse && this.$gate.allows('viewAny', ['Attendance', this.course])) ||
                    this.$gate.allows('viewAny', ['QuizDashboard']) ||
                    this.canShowScheduleMenu
                );
            }

            return (
                this.hasNotesMenu ||
                this.hasCourseReportsMenu ||
                (!this.isSelfPacedCourse && this.$gate.allows('viewAny', ['Attendance', this.course])) ||
                this.$can('view all', 'meetings') ||
                this.$gate.allows('viewAny', ['AttemptAuthorization', this.course]) ||
                this.$gate.allows('viewAny', ['QuizDashboard']) ||
                this.$gate.allows('viewReports', this.course) ||
                this.$gate.allows('viewAny', 'Remediation') ||
                this.$gate.allows('viewAny', ['CourseLogEntry', this.course]) ||
                this.canShowScheduleMenu
            );
        },

        scheduleMenuLabel() {
            return 'Due Dates and Schedule';
        },

        scheduleMenuRouteName() {
            return 'course-activities-view-upcoming';
        },

        isMeetingsSectionActive() {
            return isCourseMeetingsRouteName(this.$route.name);
        },

        canShowGradebookPreview() {
            return (
                this.isCourseTemplate &&
                this.courseTemplate &&
                this.$gate.allows('viewTemplatePreview', ['Gradebook', this.courseTemplate])
            );
        },
    },

    methods: {
        get,

        nestedListItemKey,

        /**
         * @param event
         */
        open(event) {
            this.$emit('open', event);
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

$divider-height: 3px;

/* Ensure all ULs in the sidebar summary take full width and center content */
ul {
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
}

.is-sidebar-summary-item {
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-title {
        color: $text-subtle;
        display: block;
        font-size: $size-10;
        line-height: 1;
        margin-top: 0.3rem;
    }

    &-button {
        margin-top: $column-gap;
    }

    &.is-collection {
        margin-bottom: 0;
        position: relative;

        /* Nested UL within collection should also be full width */
        > ul {
            width: 100%;
        }

        &::before {
            background: var(--primary-color);
            content: '';
            display: block;
            height: $divider-height;
            margin: ($column-gap * 0.75) auto;
            width: 50%;
            opacity: 0.5;
        }

        &:first-child::before {
            display: none;
        }

        .is-sidebar-summary-item:first-child .button {
            margin-top: 0;
        }
    }

    &.is-active {
        position: relative;

        .button::after {
            background: var(--primary-color);
            border-radius: 3px;
            content: '';
            height: 6px;
            position: absolute;
            right: #{calc($column-gap / -1)};
            top: 50%;
            transform: translateY(-50%);
            width: 6px;
        }

        .is-sidebar-summary-item-title {
            color: var(--primary-color);
        }
    }

    .button {
        &.is-small {
            box-shadow: none !important;
        }
    }
}
</style>
